import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import ShopifyHeader from './ShopifyHeader';
import { useNavigate } from 'react-router-dom';
const ShopifyApp = () => {
    const [searchParams] = useSearchParams();
    const { shop: shopParam } = useParams(); // Get shop from URL path
    const [isLoading, setIsLoading] = useState(true);
    const [shopData, setShopData] = useState(null);
    const [error, setError] = useState('');
    const [scopeStatus, setScopeStatus] = useState(null);
    const [showScopeBanner, setShowScopeBanner] = useState(false);
    const navigate = useNavigate();
    // Get shop from either path parameter or query parameter
    const shop = shopParam ? `${shopParam}.myshopify.com` : searchParams.get('shop');
    const host = searchParams.get('host');

    useEffect(() => {
        if (!shop) {
            setError('Missing shop parameter. Please reinstall the app.');
            setIsLoading(false);
            return;
        }

        // Initialize the app interface
        initializeApp();
        // Check scope status
        checkScopeStatus();
    }, [shop, host]); // eslint-disable-line react-hooks/exhaustive-deps

    const checkScopeStatus = async () => {
        try {
            const response = await fetch(
                `https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/shopify/api/reinstall?shop=${shop}`
            );
            const data = await response.json();
            setScopeStatus(data);
            setShowScopeBanner(data.needs_update);
        } catch (err) {
            console.error('Error checking scopes:', err);
        }
    };

    const handleReinstall = () => {
        if (scopeStatus?.oauth_url) {
            window.location.href = scopeStatus.oauth_url;
        }
    };

    const initializeApp = async () => {
        try {
            setIsLoading(true);

            // Check authentication status
            const { ShopifyAuth } = await import('../utils/shopifyAuth');
            const authData = ShopifyAuth.getAuthData();

            if (!authData.isAuthenticated) {
                setError('Authentication expired. Please reinstall the app.!');
                setIsLoading(false);
                return;
            }

            console.log('Auth data:', authData);

            // Here you would:
            // 1. Verify the shop installation with backend
            // 2. Load shop-specific data using the auth token
            // 3. Initialize services

            setTimeout(() => {
                setShopData({
                    shopDomain: shop,
                    displayName: shop.replace('.myshopify.com', ''),
                    installedAt: new Date().toLocaleDateString(),
                    status: 'active',
                    authToken: authData.token ? '✅ Valid' : '❌ Missing'
                });
                setIsLoading(false);
            }, 1500);

        } catch (err) {
            console.error('App initialization error:', err);
            setError('Failed to initialize app. Please try again.');
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="text-center">
                    <Spinner animation="border" variant="success" size="lg" />
                    <h4 className="mt-3">Loading ALANI360 App...</h4>
                    <p className="text-muted">MELI Marketplace Integration</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col md={6}>
                        <Alert variant="danger">
                            <Alert.Heading>Application Error</Alert.Heading>
                            <p>{error}</p>
                            <Button variant="outline-danger" href="/shopify">
                                Return to Installation
                            </Button>
                        </Alert>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <div style={{ minHeight: '100vh' }}>
            <ShopifyHeader />

            <Container className="py-4">
                {/* Scope Update Banner */}
                {showScopeBanner && scopeStatus && (
                    <Alert variant="warning" dismissible onClose={() => setShowScopeBanner(false)} className="mb-4">
                        <Alert.Heading>📋 Permission Update Required</Alert.Heading>
                        <p>
                            New features require additional permissions: <strong>{scopeStatus.missing_scopes?.join(', ') || 'read_metaobjects'}</strong>
                        </p>
                        <p className="mb-0">
                            <Button variant="warning" size="sm" onClick={handleReinstall}>
                                Update Permissions Now
                            </Button>
                            {' '}
                            <small className="text-muted">This will redirect you to Shopify to approve new permissions.</small>
                        </p>
                    </Alert>
                )}

                {/* Welcome Section */}


                {/* Quick Actions */}
                <Row className="g-4">
                    <Col md={4}>
                        <Card className="h-100">
                            <Card.Body className="text-center">
                                <i className="fas fa-sync-alt fa-3x text-success mb-3"></i>
                                <h5>Sync Products</h5>
                                <p className="text-muted">
                                    Import and sync your Shopify products with ALANI360
                                </p>
                                <Button variant="success" className="w-100"
                                    onClick={() => {
                                        const shop = shopData?.displayName;
                                        navigate(`/shopify/${shop}/products`)
                                    }}
                                >
                                    Sync Products
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="h-100">
                            <Card.Body className="text-center">
                                <i className="fas fa-shopping-cart fa-3x text-primary mb-3"></i>
                                <h5>Manage Orders</h5>
                                <p className="text-muted">
                                    View and manage orders from all your sales channels
                                </p>
                                <Button variant="primary" className="w-100"
                                    onClick={() => {
                                        const shop = shopData?.displayName;
                                        navigate(`/shopify/${shop}/orders`)
                                    }}
                                >
                                    View Orders
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="h-100">
                            <Card.Body className="text-center">
                                <i className="fas fa-cog fa-3x text-secondary mb-3"></i>
                                <h5>Settings</h5>
                                <p className="text-muted">
                                    Configure sync preferences<br /> and integration settings
                                </p>
                                <Button variant="secondary" className="w-100 mb-2"
                                    onClick={() => {
                                        const shop = shopData?.displayName;
                                        navigate(`/shopify/${shop}/settings`)
                                    }}
                                >
                                    Open Settings
                                </Button>
                                <Button variant="outline-primary" size="sm" className="w-100" onClick={handleReinstall}>
                                    🔄 Update App Permissions
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>


        </div>
    );
};

export default ShopifyApp;
